import { useEffect, useState } from 'react'
import { reportError } from './telemetry'

/**
 * The watchdog over the stages this page runs BEFORE a scan can start, and the reload budget it
 * spends (docs/decisions.md D-46).
 *
 * Three stages stand between a patient opening their link and the start screen: the runtime config
 * fetch, the link handshake, and the scan config fetch. Each is one small HTTP request, none of them
 * was bounded by anything, and a request that never settles is a page that says "Loading…" until the
 * patient gives up. That is the reported fault, and the thing patients found that fixes it is a
 * manual refresh - so this does the refresh for them.
 *
 * A FOURTH STAGE IS THE VENDOR PROLOGUE, and it is the one patients were actually reporting: the
 * scanning screen is up, the bar is indeterminate, and the SDK has not started yet. It was bounded
 * only by three minutes (D-34), which a patient cannot tell from forever, so they refreshed out of
 * scans that were still loading. It is watched differently from the three above and by a different
 * rule - see {@link VENDOR_SILENCE_WINDOW_MS} - because the three are small requests and this one is
 * tens of megabytes.
 *
 * TWENTY SECONDS PER STAGE. Every stage this covers is a single request for a few hundred bytes, so
 * twenty seconds is not a guess at how slow a phone can be - it is an order of magnitude beyond the
 * slowest honest answer, which is what keeps the watchdog off a connection that is merely bad. A
 * stage still running at twenty seconds is one whose response is not coming.
 *
 * TWO AUTOMATIC RELOADS, NOT UNLIMITED. A page that reloads itself whenever a stage is slow can
 * reload itself forever, and a reload loop is a worse page than a stuck one: on every pass where the
 * tab holds no verified token it spends one of the five verifies an hour D-45 exists to protect, it
 * makes the browser's own back button useless, and it gives the patient nothing to read and nothing
 * to tap.
 * Two is the number of attempts a patient makes by hand before they call somebody, and the third
 * stall is a fault that reloading does not fix - so the third one says so and offers the reload as a
 * choice instead of taking it.
 *
 * SESSIONSTORAGE, keyed per link, exactly as `scan-session.ts` is and for the same reasons. The
 * budget has to survive a reload or it is not a budget, which rules out memory; and it has to be
 * forgotten when the tab is, or a patient who stalls once in the morning meets an exhausted budget
 * on a fresh link in the afternoon, which rules out localStorage. Keyed per link because the budget
 * belongs to the attempt at one scan, not to the browser.
 *
 * Every access is guarded, because storage is not promised - a private window, a browser set to
 * block site data. Unlike the held token, though, a budget that cannot be written is not merely a
 * lost optimisation: it is a counter that reads zero on every pass, which is precisely the reload
 * loop above. So a page that cannot remember does not reload itself at all and goes straight to the
 * screen that offers the reload. That patient is still strictly better off than before, because the
 * page they had was one that never stopped loading.
 *
 * ONLY WHILE THE PATIENT IS LOOKING. A phone that has been sent to another app suspends timers and
 * connections, so twenty seconds of a backgrounded tab is not twenty seconds of a stalled request -
 * it is a patient who went to answer a message. The window therefore runs only while the document is
 * visible and starts over when the patient comes back, which also settles the uglier half: a page
 * that reloaded itself while hidden would spend the budget somewhere the patient could not see it
 * being spent, and hand them a fresh "Loading…" for their trouble.
 */
export const STALL_WINDOW_MS = 20_000

/**
 * The window over the vendor prologue - the download and the licence call between the patient's tap
 * and the measurement - and A WINDOW OF SILENCE rather than a deadline.
 *
 * It is not a second twenty seconds, because the thing being waited for is not a small request: 33 MB
 * of Shen.AI runtime, or 55 MB of Mindset bundle, on a phone on cellular. A deadline over that is the
 * mistake D-34 records - telling a patient the scanner could not load on a scan that was working - and
 * it is the reason nothing here starts a clock at the tap. What is timed is the gap between the
 * vendor's own reports: Shen.AI reports the fraction of its runtime downloaded as it arrives, so a
 * download that is merely slow keeps speaking and a stalled one goes quiet, and telling those apart is
 * the whole of this decision.
 *
 * FORTY-FIVE SECONDS because it has to be longer than the longest step a healthy prologue is silent
 * for - which is instantiating a runtime after its last byte has landed, unmeasured here on the
 * slowest phone a patient might hold - and the answer to an unmeasured number is a window well past
 * any plausible value for it rather than a guess at it. It is still a quarter of the three minutes
 * that same download is allowed to take before the vendor's own budget gives up (D-34), which is the
 * wait patients were refreshing out of.
 */
export const VENDOR_SILENCE_WINDOW_MS = 45_000

export const MAX_AUTOMATIC_RELOADS = 2

/**
 * How long a spent budget is remembered, measured from the MOST RECENT automatic reload.
 *
 * The rule it makes is "at most two automatic reloads in any five minutes" rather than "two per tab,
 * ever". Without it the dead-end screen is permanent for the life of the tab: a patient who stalled
 * twice at nine o'clock and came back to the same tab at noon met a page that had already given up on
 * their behalf, on a network fault that had long since cleared. Five minutes because it is longer than
 * the whole journey it bounds - three small requests, a download, a licence call - so a budget can
 * only still be alive while the trouble that spent it is, and short enough that a patient who walks
 * away and comes back is not being punished for the state of the building's wifi ten minutes ago.
 *
 * Sliding rather than fixed: each reload restarts it. Two reloads five minutes apart are not the loop
 * this budget exists to prevent, and a window that expired from the FIRST of them would hand the
 * second one a fresh two the moment it was spent.
 */
export const RELOAD_BUDGET_WINDOW_MS = 5 * 60_000

/**
 * The stages a stall can be recovered from, and the whole of the list. Nothing terminal is here:
 * reloading a screen that has decided something - a dead link, a date of birth being typed, a
 * finished scan - takes the page away from a patient who is not waiting for anything.
 *
 * `vendor-prologue` is the scanning screen BEFORE the measurement, which is where the stall patients
 * actually reported is: the video frame is up, the bar is indeterminate, and the SDK has not started.
 * It is not the whole of `scanning` and it never can be - once a vendor is measuring, the patient is
 * holding still for a minute they cannot be asked to spend twice - and which vendor and which phase
 * it covers is `scan-flow.tsx`'s answer, because silence means different things to different SDKs.
 */
export type PreparationStage = 'runtime-config' | 'challenging' | 'loading-config' | 'vendor-prologue'

function windowFor(stage: PreparationStage): number {
  return stage === 'vendor-prologue' ? VENDOR_SILENCE_WINDOW_MS : STALL_WINDOW_MS
}

/**
 * The reload, behind a name.
 *
 * A property on an exported object rather than a bare function so that a test can watch it the way
 * this suite already watches `Storage.prototype` - jsdom cannot follow a real navigation, and a
 * production branch that skips the reload when it thinks it is being tested would be a gate that
 * measures the wrong page.
 */
export const thisPage = {
  reload: () => window.location.reload(),
}

/**
 * What the budget has cost so far, and zero for a record that has expired, that this tab cannot read,
 * or that it does not recognise.
 *
 * A RECORD RATHER THAN A NUMBER, which is what makes the expiry possible: a bare count says a tab
 * once reloaded itself twice and never says when, so the dead-end screen it leads to is permanent.
 * The count that reads zero on the far side of {@link RELOAD_BUDGET_WINDOW_MS} is the same answer a
 * fresh tab gets, which is the point - a stall half an hour later is a new stall.
 *
 * A tab already holding the bare number this replaced reads as zero here rather than as anything at
 * all: the guards a malformed record already needed cover the old shape without a migration, and the
 * cost of the one time a tab forgets a spent reload is one more reload than the rule allows.
 *
 * `now` is a parameter for the reason `scan-session.ts` gives: a test drives the clock instead of
 * waiting on it.
 */
export function readStallReloads(token: string, now: number = Date.now()): number {
  try {
    const raw = sessionStorage.getItem(keyFor(token))
    if (raw === null) return 0
    const spent = JSON.parse(raw) as StoredStallReloads
    if (!Number.isInteger(spent.count) || spent.count < 0 || typeof spent.at !== 'number') return 0
    return now - spent.at < RELOAD_BUDGET_WINDOW_MS ? spent.count : 0
  } catch {
    return 0
  }
}

/**
 * Spends one reload, and answers whether it was actually remembered - a `setItem` that throws and one
 * that silently keeps nothing are the same fact to the caller, and either one turns the budget into a
 * counter stuck at zero.
 *
 * The moment stored is THIS reload's, so the window slides with the trouble rather than with the
 * first sign of it.
 */
export function recordStallReload(token: string, now: number = Date.now()): boolean {
  const spent: StoredStallReloads = { count: readStallReloads(token, now) + 1, at: now }
  try {
    sessionStorage.setItem(keyFor(token), JSON.stringify(spent))
  } catch {
    return false
  }
  return readStallReloads(token, now) === spent.count
}

export function forgetStallReloads(token: string): void {
  try {
    sessionStorage.removeItem(keyFor(token))
  } catch {
    // Nothing was stored, or nothing can be.
  }
}

/**
 * Watches one stage, and answers whether this page has run out of reloads while waiting on it.
 *
 * `stage` is null wherever the page is not waiting on a preparation stage, which is what keeps the
 * watchdog off every screen listed on {@link PreparationStage} - and, on the scanning screen, off
 * every vendor and phase whose silence is not evidence of anything (scan-flow.tsx).
 *
 * GIVING UP BELONGS TO ONE VISIT TO ONE STAGE, and not to the stage and not to the tab. A request
 * that was merely very late rather than lost takes the patient onward the moment it lands, and a
 * stage the flow comes back to is waited for from zero. The flow does come back: a 401 on the scan
 * config, or on a submit, calls `verifyAgain()`, which sends `loading-config` to `challenging` and
 * round again. Remembering which stage had given up - rather than that this visit to it had - meant
 * that second, healthy handshake rendering "This page could not load" for its whole duration, with a
 * button under it that throws the in-flight request away. Latching a plain boolean forever, the
 * shape before that, was the same fault with no way out of it at all.
 *
 * The reset is made during render rather than in the effect below because an effect runs after the
 * browser has painted, and what it would paint is one frame of the could-not-load screen on the way
 * into a stage nothing is yet wrong with.
 */
export function useStallWatchdog(
  token: string | undefined,
  stage: PreparationStage | null,
  /**
   * A number that CHANGES whenever the watched stage has shown a sign of life, which restarts the
   * window without disturbing anything else.
   *
   * The three request stages have no sign of life to give - a request answers or it does not - and
   * leave this at its default. The vendor prologue has one, and needs it: what is being told apart
   * there is a download that is slow from a download that has stopped, and the only thing that
   * distinguishes them is whether the vendor is still saying anything at all.
   */
  progressed: number = 0,
): boolean {
  const [watchedStage, setWatchedStage] = useState(stage)
  const [exhausted, setExhausted] = useState(false)

  if (watchedStage !== stage) {
    setWatchedStage(stage)
    setExhausted(false)
  }

  useEffect(() => {
    if (token === undefined || stage === null) return
    const watched = token
    const waitingOn = stage
    const stageWindow = windowFor(stage)

    let watchdog: ReturnType<typeof setTimeout> | undefined

    function restartWhileVisible() {
      clearTimeout(watchdog)
      if (document.visibilityState !== 'visible') return

      watchdog = setTimeout(() => {
        if (readStallReloads(watched) >= MAX_AUTOMATIC_RELOADS || !recordStallReload(watched)) {
          reportError(new Error('A preparation stage stalled'), { stage: waitingOn, recovery: 'gave up' })
          setExhausted(true)
          return
        }
        reportError(new Error('A preparation stage stalled'), { stage: waitingOn, recovery: 'reload' })
        thisPage.reload()
      }, stageWindow)
    }

    restartWhileVisible()
    document.addEventListener('visibilitychange', restartWhileVisible)

    return () => {
      clearTimeout(watchdog)
      document.removeEventListener('visibilitychange', restartWhileVisible)
    }
  }, [token, stage, progressed])

  return exhausted
}

interface StoredStallReloads {
  readonly count: number
  /** The most recent automatic reload, which is what {@link RELOAD_BUDGET_WINDOW_MS} runs from. */
  readonly at: number
}

function keyFor(token: string): string {
  return `stall-reloads:${token}`
}
